import { AppToastOutlet, useAppToastRef } from './providers/app-toast-provider';
import { useAdminTaskSummary } from './general/use-admin-task-count';
import './assets/admin-toolbar.css';
import './assets/management-page.css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi } from "./providers/api-provider";

import { Avatar } from 'primereact/avatar';
import { BreadCrumb } from 'primereact/breadcrumb';
import { Button } from 'primereact/button';
import { Menubar } from 'primereact/menubar';
import { PanelMenu } from 'primereact/panelmenu';
import { Sidebar } from 'primereact/sidebar';

import { getUserProfile, isAdminRole, UserProfile } from './general/user';
import Config, { RouteMap } from './general/config';
import { ThemeAppearance } from './general/themeSwitcher';
import { Menu } from 'primereact/menu';
import { UserSettingsDialog } from './components/user-settings-dialog';
import { SocialShareLinks } from './components/social-share-links';
import { applyUserPreferences, browserUserPreferences } from './general/user-preferences';
import { useInputWordCompletion } from './components/word-completion-toggle';
import { Api } from './general/api';
import { PostLoginResponseDto, UserDto } from './general/dtos';
import { useUserStore } from './components/zustand-stores';
import { IAuth, useAuth } from './providers/auth-provider';
import { navigationMenuItem } from './general/navigation-menu-item';
import { forumThemeUrl, useForumThemeMode } from './general/forum-theme';

export default function MainLayout({ children }: { children: React.ReactNode }) {
  const auth: IAuth = useAuth();
  const isAuthenticated: boolean = auth.getIsAuthenticated();
  const isLoading: boolean = auth.getIsLoading();
  const user = auth.getUser();

  const api: Api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicCertificate = /^\/certificate\/[^/]+\/?$/.test(location.pathname);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const forumMode = useForumThemeMode();
  const toastRef = useAppToastRef();
  const accountMenuRef = useRef<Menu>(null);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false);
  useInputWordCompletion(isAuthenticated && preferencesReady ? document : null, false);
  const [profile, setProfile] = useState<UserProfile>({firstName: '', lastName: '', avatar: '' });
  const userDto: UserDto | null = useUserStore((s) => s.user);

  function useIsMobile(breakpoint: number = 960): boolean {
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= breakpoint);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [breakpoint]);

    return isMobile;
  }
  const isMobile: boolean = useIsMobile();

  const isAdmin: boolean = isAdminRole(user);
  const taskSummary = useAdminTaskSummary(isAdmin && isAuthenticated && !isLoading && !isPublicCertificate, user?.sub, location.pathname);
  const taskCount = taskSummary?.total ?? null;
  const taskLabel = taskCount === null ? 'Admin tasks' : `Admin tasks (${taskCount})`;
  const manageLabel = taskCount === null ? 'Manage' : `Manage (${taskCount})`;
  const reviewCount = taskSummary?.counts?.reviews ?? null;
  const financeCount = taskSummary?.counts ? taskSummary.counts.payments + taskSummary.counts.refunds : null;
  const reviewLabel = reviewCount === null ? 'Reviews' : `Reviews (${reviewCount})`;
  const financeLabel = financeCount === null ? 'Finance' : `Finance (${financeCount})`;
  const taskDanger = (taskCount ?? 0) > 0 ? 'admin-menu-danger' : undefined;
  const reviewDanger = (reviewCount ?? 0) > 0 ? 'admin-menu-danger' : undefined;
  const financeDanger = (financeCount ?? 0) > 0 ? 'admin-menu-danger' : undefined;

  const effectiveUserData = (): UserProfile => {
    return userDto ? { firstName: userDto.first_name, lastName: userDto.last_name, avatar: userDto.picture } : profile;
  }

  async function postLogin(isCurrent: () => boolean) {
    let userInfo: UserDto | null = null;
    try {
      const data: PostLoginResponseDto = await api.postLogin(user?.given_name, user?.family_name, user?.picture, user?.email);
      if (!isCurrent()) return;
      userInfo = data.user;
      useUserStore.getState().setUser(data.user);
      let preferences = data.user.preferences;
      if (!preferences) {
        // Adopt existing browser choices once, without copying another account's choices.
        let owner: string | null = null;
        try { owner = localStorage.getItem('triad.preferences-owner'); } catch { /* Optional cache. */ }
        preferences = owner && owner !== user?.sub ? {theme: 'lara-light-blue', wordSuggestions: false} : browserUserPreferences();
        preferences = await api.saveMyPreferences(preferences);
      }
      if (!isCurrent()) return;
      // Account preferences take precedence over this browser's previous visitor.
      applyUserPreferences(preferences);
      try { localStorage.setItem('triad.preferences-owner', user?.sub ?? ''); } catch { /* Optional cache. */ }
      setPreferencesReady(true);
    } catch (error: any) {
      console.error("Error in postLogin:", error);
    }
    if (!isCurrent()) return;

    const token: string = ''; // await getAccessTokenSilently(); undecoded, not needed for now
    const userProfile: UserProfile = getUserProfile(user, token);
    setProfile(userProfile);

    const userFullName: string | null = user ? (userInfo
        ? `${userInfo?.first_name ?? ''} ${userInfo?.last_name ?? ''}`
        : `${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`).trim() : null;
    toastRef.current?.show({
      severity: 'success',
      summary: 'Welcome!',
      detail: `Hello, ${userFullName ?? 'user'}!`,
      life: 3000
    });

  }

  useEffect(() => {
    let current = true;
    setPreferencesReady(false); setSettingsVisible(false);
    if (isAuthenticated && !isLoading && !isPublicCertificate) {
      // call the postLogin endpooint to log the login event
      postLogin(() => current);
    }
    return () => { current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading, isPublicCertificate, user?.sub]);
 
  // Breadcrumb items generation
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const breadcrumbItems = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    return navigationMenuItem(RouteMap[path] || segment, '', path, navigate);
  });
  const home = navigationMenuItem('Home', 'pi pi-home', '/', navigate);
  // Breadcrumb items generation end

  const toolbarStart = (
    <div className="flex align-items-center gap-2">
    <img src={'../assets/logo.png'} style={{ width: "40px" }} alt=''/>
    {/* <Button
      icon="pi pi-bars"
      rounded
      text
      aria-label="Toggle Sidebar"
      onClick={() => setSidebarVisible(true)}
    /> */}
      <span className="text-xl font-bold text-primary">{Config.app_name}</span>
    </div>
  );

  const effective: UserProfile = effectiveUserData();
  const userFullName = user ? `${effective?.firstName ?? ''} ${effective?.lastName ?? ''}`.trim() : '';

  const accountItems = [
    { label: 'Profile', icon: 'pi pi-user', command: () => navigate('/profile') },
    { label: 'Settings', icon: 'pi pi-cog', disabled: !preferencesReady,
      title: !preferencesReady ? 'Settings will be available after your account finishes loading.' : undefined,
      command: () => setSettingsVisible(true) },
    { separator: true },
    { label: 'Log out', icon: 'pi pi-sign-out', command: () => {
      setSettingsVisible(false); setPreferencesReady(false);
      auth.logout(); useUserStore.getState().clearUser();
    } },
  ];
  const toobarEnd = isAuthenticated ? (<>
    <div className="flex align-items-center gap-2">
      {Config.showBreadcrumbs && <BreadCrumb model={breadcrumbItems} home={home} />}
      <SocialShareLinks pathname={location.pathname} search={location.search}/>
      <button type="button" className="account-menu-trigger" aria-label="Account menu"
        aria-haspopup="menu" aria-expanded={accountMenuOpen} aria-controls="account-menu" onClick={event => accountMenuRef.current?.toggle(event)}>
        <Avatar image={effective?.avatar} icon={!effective?.avatar ? 'pi pi-user' : undefined} shape="circle" />
        {!isMobile && <span>{userFullName || user?.email || 'Account'}</span>}
        <i className="pi pi-angle-down" aria-hidden="true" />
      </button>
      <Menu id="account-menu" model={accountItems} popup ref={accountMenuRef}
        onShow={() => setAccountMenuOpen(true)} onHide={() => setAccountMenuOpen(false)} />
    </div>
  </>) : (<>
    <div className="guest-account-controls flex align-items-center gap-2">
      <SocialShareLinks pathname={location.pathname} search={location.search}/>
      <Button
        label="Login"
        aria-label="Login" title="Login"
        icon="pi pi-sign-in"
        className="p-button-sm md:p-button"
        style={{ whiteSpace: "nowrap" }}
        onClick={() => auth.login()/*loginWithRedirect({ authorizationParams: { scope: "openid profile email" }})*/}
      />
      <Button
        label="Sign up"
        aria-label="Sign up" title="Sign up"
        icon="pi pi-user-plus"
        className="p-button-sm md:p-button"
        style={{ whiteSpace: "nowrap" }}
        severity="secondary"
        onClick={() => auth.signup()/*loginWithRedirect({ authorizationParams: { screen_hint: "signup" }})*/}
      />
    </div>
  </>);

  const matchesRoute = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const manageClassName = [taskDanger, matchesRoute('/admin') ? 'route-active' : undefined].filter(Boolean).join(' ') || undefined;
  const manageItems = (mobile = false) => {
    const item = (label: string, icon: string, path: string, danger?: string) => ({
      ...navigationMenuItem(label, icon, path, navigate, mobile ? () => setSidebarVisible(false) : undefined),
      className: [danger, (matchesRoute(path) || (path === '/admin/modules' && matchesRoute('/admin/managedModuleEditor')))
        ? 'admin-route-active' : undefined].filter(Boolean).join(' ') || undefined,
    });
    return [
      item(taskLabel, 'pi pi-list-check', '/admin/tasks', taskDanger),
      item('Users', 'pi pi-users', '/admin/users'),
      item('Modules', 'pi pi-th-large', '/admin/modules'),
      item('Courses', 'pi pi-th-large', '/admin/courses'),
      { separator: true },
      item('Certificates', 'pi pi-id-card', '/admin/certificates'),
      item(reviewLabel, 'pi pi-star', '/admin/reviews', reviewDanger),
      item('Themes', 'pi pi-palette', '/admin/themes'),
      item(mobile ? 'Protected storage' : 'Storage', 'pi pi-folder', '/admin/protectedStorage'),
      ...(mobile ? [] : [{ separator: true }]),
      item(financeLabel, 'pi pi-wallet', '/admin/finance', financeDanger),
      item('Analytics', 'pi pi-chart-line', '/admin/analytics'),
      item('Activities', 'pi pi-list-check', '/admin/activities'),
      { separator: true },
      item('Help', 'pi pi-question-circle', '/admin/help'),
      ...(Config.env === 'local' ? [item('Debug', 'pi pi-code', '/admin/debug')] : []),
    ];
  };

  // Forum is a separate application: follow its URL using normal browser navigation.
  const forumItems = Config.forumUrl ? [{ label: 'Forum', icon: 'pi pi-comments', url: forumThemeUrl(Config.forumUrl, forumMode), target: '_blank' }] : [];
  const toolbarItems = [
    ...[
      ['Home', 'pi pi-home', '/'],
      ['Products', 'pi pi-shopping-cart', '/products'],
      ['Pricing', 'pi pi-tag', '/pricing'],
      ...(Config.showServicesMenu ? [['Services', 'pi pi-briefcase', '/services']] : []),
      ['Reviews', 'pi pi-star', '/reviews'],
    ].map(([label, icon, path]) => ({
      ...navigationMenuItem(label, icon, path, navigate),
      className: location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`))
        ? 'route-active' : undefined,
    })),
    ...forumItems,
    ...(isAdmin
    ? [
      {
        label: manageLabel, icon: 'pi pi-cog', className: manageClassName,
        items: manageItems()
      },
    ]
    : []),
    {
      ...navigationMenuItem('About', 'pi pi-info-circle', '/about', navigate),
      className: location.pathname === '/about' || location.pathname.startsWith('/about/') ? 'route-active' : undefined,
    },
  ];

  const menuItems = [
    {
      label: 'Navigation',
      expanded: true,
      items: [
        navigationMenuItem('Home', 'pi pi-home', '/', navigate, () => setSidebarVisible(false)),
        navigationMenuItem('Products', 'pi pi-shopping-cart', '/products', navigate, () => setSidebarVisible(false)),
        navigationMenuItem('Pricing', 'pi pi-tag', '/pricing', navigate, () => setSidebarVisible(false)),
        ...(Config.showServicesMenu ? [navigationMenuItem('Services', 'pi pi-briefcase', '/services', navigate, () => setSidebarVisible(false))] : []),
        navigationMenuItem('Reviews', 'pi pi-star', '/reviews', navigate, () => setSidebarVisible(false)),
        ...forumItems,
        ...(isAdmin
        ? [
          { separator: true },
          {
            label: manageLabel, icon: 'pi pi-cog', className: manageClassName,
            expanded: true,
            items: manageItems(true)
          },
        ]
        : []),
        navigationMenuItem('About', 'pi pi-info-circle', '/about', navigate, () => setSidebarVisible(false)),
      ]
    }
  ];

  return (
    <div className="layout-container">
      <div className="layout-header">
        <Menubar model={toolbarItems} start={toolbarStart} end={toobarEnd} className="shadow-2" />
      </div>

      <Sidebar
        visible={sidebarVisible}
        dismissable={true}
        onHide={() => setSidebarVisible(false)}
        modal={false}
        showCloseIcon={true}
        className="p-sidebar-sm"
      >
        <div className="flex justify-content-between align-items-center mb-3">
          <span className="text-lg font-bold">Menu</span>
        </div>
        <PanelMenu model={menuItems} className="w-15rem" />
      </Sidebar>

      <ThemeAppearance />
      {settingsVisible && <UserSettingsDialog onHide={() => setSettingsVisible(false)}
        onSaved={() => toastRef.current?.show({severity: 'success', summary: 'Settings saved', life: 2500})} />}
      <AppToastOutlet toastRef={toastRef} />

      <main className={`layout-content p-4${location.pathname.startsWith('/admin/') ? ' layout-content-management' : ''}`}>{children}</main>
    </div>
  );
}
